import { Controller, useFormContext } from "react-hook-form";

import { CountryField } from "@ui/components/AddressFormProvider";
import { TextInput } from "@ui/components/Inputs";
import { PostCode } from "@ui/components/PostCode";
import { useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import type { PostCodeConfig } from "@jti/magento";

import styles from "./TaiwanAddressForm.module.scss";

type TaiwanAddressFormProps = {
  fieldName?(name: string): string;
  postCodeConfig?: PostCodeConfig;
};

export const TaiwanAddressForm = ({ fieldName = (name) => name, postCodeConfig }: TaiwanAddressFormProps) => {
  const { __ } = useTranslations();
  const { COUNTRY_NAME } = useConfig();
  const { control } = useFormContext();

  return (
    <div className={styles.wrapper}>
      <PostCode fieldName={fieldName} config={postCodeConfig} />
      <Controller
        control={control}
        name={fieldName("city")}
        rules={{ required: __("form_validation.field_required") }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            isError={!!error}
            errorText={error?.message}
            required
            data-testid="cityTaiwan"
            label={__("address_fields.city")}
            placeholder={__("address_fields.city_placeholder")}
            autoComplete="address-level2"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("district")}
        rules={{ required: __("form_validation.field_required") }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            isError={!!error}
            errorText={error?.message}
            required
            data-testid="districtTaiwan"
            label={__("address_fields.district")}
            placeholder={__("address_fields.district_placeholder")}
            autoComplete="address-level3"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("streetName")}
        rules={{ required: __("form_validation.field_required") }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            isError={!!error}
            errorText={error?.message}
            required
            data-testid="streetNameTaiwan"
            label={__("address_fields.street_name")}
            placeholder={__("address_fields.street_name_placeholder")}
            autoComplete="address-line1"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("streetAdditional")}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            isError={!!error}
            errorText={error?.message}
            data-testid="streetAdditionalTaiwan"
            label={__("address_fields.street_additional")}
            placeholder={__("address_fields.street_additional_placeholder")}
            autoComplete="address-line2"
          />
        )}
      />
      <CountryField fieldName={fieldName} defaultValue={COUNTRY_NAME} />
    </div>
  );
};
